import type { FiltersMetadata } from '@reports/infrastructure/repositories/types';
import type { BlockDataRequestDto } from '@reports/application/dtos';
import { createReportDateRangeFromPreset } from '@reports/domain/value-objects';
import { DateRangePresets } from '@reports/domain/types';

/**
 * Checks if filters should be cleared by comparing stored metadata with current context
 *
 * Compares stored metadata (store UUID and report path) with current values.
 * Returns true if filters should be cleared due to context changes.
 *
 * @param {FiltersMetadata | null} storedMetadata - Metadata from storage (null if not found)
 * @param {string | undefined} currentStoreUuid - Current store UUID
 * @param {string} currentReportPath - Current report path (e.g., '/reports/sales')
 * @returns {boolean} True if filters should be cleared
 *
 * @example
 * const storedMetadata = await getMetadataUseCase.execute();
 * const shouldClear = shouldClearFiltersByMetadata(storedMetadata, storeUuid, location.pathname);
 *
 * if (shouldClear) {
 *   await clearUseCase.execute();
 * }
 */
export const shouldClearFiltersByMetadata = (
  storedMetadata: FiltersMetadata | null,
  currentStoreUuid: string | undefined,
  currentReportPath: string,
): boolean => {
  if (!currentStoreUuid) {
    return true;
  }

  if (!storedMetadata) {
    return false;
  }

  if (storedMetadata.storeUuid !== currentStoreUuid) {
    return true;
  }

  if (storedMetadata.reportPath !== currentReportPath) {
    return true;
  }

  return false;
};

/**
 * Recalculates date ranges for relative presets based on current date
 *
 * Only recalculates if preset is a relative preset (not DATE_RANGE).
 * This ensures that presets like "Today", "Yesterday", "This Week", etc.
 *
 * @param {BlockDataRequestDto} filters - Filters to potentially recalculate
 * @returns {BlockDataRequestDto} Filters with recalculated dates if preset is relative
 *
 * @example
 * const savedFilters = await getUseCase.execute();
 * const updatedFilters = recalculateRelativePresetDates(savedFilters);
 * // If savedFilters.preset is "today", dates will be recalculated to today's date
 */
export const recalculateRelativePresetDates = (filters: BlockDataRequestDto): BlockDataRequestDto => {
  if (filters.preset && filters.preset !== DateRangePresets.DATE_RANGE) {
    const recalculatedDateRange = createReportDateRangeFromPreset(filters.preset);
    return {
      ...filters,
      startDate: recalculatedDateRange.start,
      endDate: recalculatedDateRange.end,
    };
  }
  return filters;
};
